import React from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import { ProductPromotion } from "@src/models/product"

type Palette = {
  accent: string
  soft: string
  contrast: string
}

const TYPE_PALETTE: Record<string, Palette> = {
  flash_sale: { accent: "#F46647", soft: "rgba(244,102,71,0.14)", contrast: "#C63F1F" },
  discount: { accent: "#D8042C", soft: "rgba(216,4,44,0.12)", contrast: "#B0031F" },
  quantity_discount: { accent: "#2A5DB0", soft: "rgba(42,93,176,0.12)", contrast: "#20488C" },
  accom_gift: { accent: "#3F9E5A", soft: "rgba(63,158,90,0.14)", contrast: "#2E7A43" },
  accom_product: { accent: "#7A4FC0", soft: "rgba(122,79,192,0.14)", contrast: "#5F3AA0" }
}

const DEFAULT_PALETTE: Palette = {
  accent: "#555",
  soft: "rgba(0,0,0,0.06)",
  contrast: "#333"
}

const PromotionBadges = ({
  promotions,
  max,
  size = "sm"
}: {
  promotions?: ProductPromotion[]
  max?: number
  size?: "sm" | "md"
}) => {
  if (!promotions || promotions.length === 0) return null

  const visible = typeof max === "number" ? promotions.slice(0, max) : promotions
  const hidden = promotions.length - visible.length

  const isMd = size === "md"
  const fontSize = isMd ? 12 : 10
  const padY = isMd ? 6 : 5
  const padX = isMd ? 14 : 11

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={isMd ? 1 : 0.75}
      sx={{
        "@keyframes ciuBadgeIn": {
          from: { opacity: 0, transform: "translateY(6px) scale(0.96)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" }
        }
      }}
    >
      {visible.map((promotion, index) => {
        const palette = TYPE_PALETTE[promotion.type] || DEFAULT_PALETTE
        return (
          <Box
            key={promotion.id}
            title={promotion.name}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: isMd ? 0.75 : 0.5,
              paddingTop: `${padY}px`,
              paddingBottom: `${padY}px`,
              paddingLeft: `${padX}px`,
              paddingRight: `${padX}px`,
              maxWidth: "100%",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.82)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: `0 4px 14px ${palette.soft}, 0 1px 2px rgba(0,0,0,0.06)`,
              animation: "ciuBadgeIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both",
              animationDelay: `${index * 70}ms`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 6px 18px ${palette.soft}, 0 2px 4px rgba(0,0,0,0.08)`
              }
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                flexShrink: 0,
                background: palette.accent
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize,
                fontWeight: 700,
                lineHeight: 1.3,
                letterSpacing: "0.1px",
                color: palette.contrast,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {promotion.label || promotion.type_label}
            </Box>
          </Box>
        )
      })}
      {hidden > 0 && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            paddingY: `${padY}px`,
            paddingX: `${padX}px`,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
            fontSize,
            fontWeight: 700,
            color: DEFAULT_PALETTE.contrast
          }}
        >
          +{hidden}
        </Box>
      )}
    </Stack>
  )
}

export default PromotionBadges
